/**
 * Remotion timeline utilities — Behavioral Tension Engine
 * ---------------------------------------------------------------
 * Pacing is the product. Escalation beats LINGER. Neutral beats
 * MOVE. Resonant phrases (recognized emotional pressure lines)
 * are HELD and given ambient room. All output is frame-based at
 * 30fps. Pure functions, no Remotion imports.
 */

import type { DialogueTurn, SimulationContent } from "@/lib/contentGenerator";

export const FPS = 30;
export const WIDTH = 1080;
export const HEIGHT = 1920;

/**
 * Phrases the engine recognizes as emotionally loaded. When a turn
 * contains one, it is treated as a "resonant moment": held longer,
 * surrounding bubbles dim, motion calms, ambient glow softens.
 * These are forensic recognition points, not punchlines.
 */
export const RESONANT_PATTERNS: RegExp[] = [
  /you'?re too sensitive/i,
  /you'?re overreacting/i,
  /that never happened/i,
  /i never said that/i,
  /you'?re making this up/i,
  /you always do this/i,
  /you never/i,
  /calm down/i,
  /stop being so/i,
  /everyone thinks/i,
  /maybe you'?re the problem/i,
  /after everything i'?ve done/i,
  /i guess i'?m just/i,
  /forget it/i,
  /it'?s fine/i,
  /whatever you want/i,
];

export function isResonant(text: string): boolean {
  return RESONANT_PATTERNS.some((rx) => rx.test(text));
}

export interface TurnFrame {
  index: number;
  role: DialogueTurn["role"];
  text: string;
  spiral: boolean;
  resonant: boolean;
  /** Frame the bubble enters. */
  startFrame: number;
  /** Frame the bubble fully revealed (typewriter complete). */
  revealFrame: number;
  /** Frame the bubble exits / next begins. */
  endFrame: number;
  /** Total bubble lifetime in frames. */
  durationFrames: number;
}

export interface BeatMarker {
  /** Frame where the beat lands (for future audio cue alignment). */
  frame: number;
  /** Beat type — informs ambient bed / pulse / stinger. */
  kind: "enter" | "escalate" | "resonate" | "spiral" | "settle" | "cta";
}

export interface TimelinePlan {
  fps: number;
  width: number;
  height: number;
  totalFrames: number;
  ctaStartFrame: number;
  ctaFrames: number;
  turns: TurnFrame[];
  spiralFrame: number;
  /** Frames where resonant phrases land (for ambient isolation). */
  resonantFrames: number[];
  /** Audio-ready beat markers. */
  beats: BeatMarker[];
}

export function msToFrames(ms: number, fps: number = FPS): number {
  return Math.max(1, Math.round((ms / 1000) * fps));
}

/**
 * Pacing weights — applied on top of the generator's millisecond
 * suggestion. Escalation lingers; neutral moves; resonance holds.
 */
function paceWeight(t: DialogueTurn, resonant: boolean): {
  durMul: number;
  pauseMul: number;
} {
  if (resonant) return { durMul: 1.35, pauseMul: 2.4 };
  if (t.spiral) return { durMul: 1.2, pauseMul: 2.1 };
  if (t.role === "them") return { durMul: 1.08, pauseMul: 1.4 };
  if (t.role === "you_alt") return { durMul: 0.95, pauseMul: 1.2 };
  return { durMul: 0.92, pauseMul: 1.0 };
}

export function planTimeline(sim: SimulationContent): TimelinePlan {
  let cursor = 0;
  let spiralFrame = -1;
  const resonantFrames: number[] = [];
  const beats: BeatMarker[] = [];

  const turns: TurnFrame[] = sim.dialogue.map((t) => {
    const resonant = isResonant(t.text);
    const w = paceWeight(t, resonant);
    const dur = msToFrames(Math.round(t.durationMs * w.durMul));
    const pause = msToFrames(Math.round(t.pauseAfterMs * w.pauseMul));
    const startFrame = cursor;
    // Slower, calmer typewriter — text reveals over ~70% of the bubble
    // duration so the eye lands on a settled phrase, not a moving one.
    const revealFrame = startFrame + Math.max(10, Math.round(dur * 0.7));
    const endFrame = startFrame + dur + pause;
    cursor = endFrame;

    beats.push({ frame: startFrame, kind: "enter" });
    if (t.role === "them") beats.push({ frame: revealFrame, kind: "escalate" });
    if (resonant) {
      resonantFrames.push(revealFrame);
      beats.push({ frame: revealFrame, kind: "resonate" });
    }
    if (t.spiral && spiralFrame === -1) {
      spiralFrame = revealFrame;
      beats.push({ frame: revealFrame, kind: "spiral" });
    }
    beats.push({ frame: endFrame, kind: "settle" });

    return {
      index: t.index,
      role: t.role,
      text: t.text,
      spiral: !!t.spiral,
      resonant,
      startFrame,
      revealFrame,
      endFrame,
      durationFrames: dur + pause,
    };
  });

  const ctaFrames = msToFrames(sim.remotion.cta.durationMs);
  const ctaStartFrame = cursor;
  const totalFrames = ctaStartFrame + ctaFrames;
  beats.push({ frame: ctaStartFrame, kind: "cta" });

  return {
    fps: FPS,
    width: WIDTH,
    height: HEIGHT,
    totalFrames,
    ctaStartFrame,
    ctaFrames,
    turns,
    spiralFrame,
    resonantFrames,
    beats,
  };
}

/**
 * Returns true if the given frame sits inside a resonant "isolation
 * window" — used by templates to dim non-focal bubbles, calm motion,
 * and add ambient glow around the held phrase.
 */
export function inResonantWindow(
  frame: number,
  resonantFrames: number[],
  before = 12,
  after = 60
): { active: boolean; intensity: number; anchor: number } {
  let best = { active: false, intensity: 0, anchor: -1 };
  for (const r of resonantFrames) {
    if (frame >= r - before && frame <= r + after) {
      const span = after + before;
      const t = (frame - (r - before)) / span;
      // bell curve — peaks mid-window, fades at edges
      const intensity = Math.max(0, Math.min(1, 1 - Math.abs(t - 0.5) * 2));
      if (intensity > best.intensity) best = { active: true, intensity, anchor: r };
    }
  }
  return best;
}

/** GreyRock cinematic palette — matte black dominant, restrained accents. */
export const PALETTE = {
  bg: "#06070a",
  bgDeep: "#020306",
  panel: "rgba(16, 20, 28, 0.55)",
  panelBorder: "rgba(120, 200, 220, 0.14)",
  cyan: "#67e8f9",
  cyanSoft: "rgba(103, 232, 249, 0.42)",
  gold: "#c9a96a",
  goldSoft: "rgba(201, 169, 106, 0.32)",
  silver: "#cdd6e0",
  textDim: "#6a7785",
  textDimmer: "#3f4854",
  themBubble: "rgba(190, 70, 75, 0.08)",
  themBorder: "rgba(220, 90, 90, 0.38)",
  youBubble: "rgba(103, 232, 249, 0.06)",
  youBorder: "rgba(103, 232, 249, 0.32)",
  altBubble: "rgba(110, 231, 183, 0.07)",
  altBorder: "rgba(110, 231, 183, 0.36)",
  spiral: "#c9a96a",
};

/** Forensic end-frame copy presets — quiet, archival. */
export const END_FRAMES = {
  archived: {
    headline: "Behavioral sequence preserved.",
    subline: "GreyRock AI®",
    micro: "Observed · Not predictive",
  },
  progression: {
    headline: "Observed Progression Archived.",
    subline: "GreyRock AI®",
    micro: "Forensic communication training",
  },
  practice: {
    headline: "Practice the conversation before it happens.",
    subline: "GreyRock AI®",
    micro: "Forensic communication training",
  },
} as const;
